import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';

/**
 * Entrada por voz.
 *
 * Graba la pregunta con el microfono, detecta sola cuando el usuario ha
 * terminado de hablar y manda el audio al servidor (POST /api/stt), que lo
 * transcribe con Whisper en la propia maquina de la universidad.
 *
 * Por que grabar y no usar el reconocimiento del navegador: el de Chrome envia
 * el audio a Google y el de Safari a Apple, y ninguno garantiza el mismo
 * resultado en los cuatro navegadores exigidos. MediaRecorder existe en
 * Chrome y Safari, escritorio y movil (Safari graba en mp4/aac, Chrome en
 * webm/opus; Whisper lee ambos).
 */

export type MicState = 'unavailable' | 'idle' | 'requesting' | 'listening' | 'transcribing';

export interface Mic {
  state: MicState;
  /** Nivel de entrada 0..1, por referencia (cambia en cada fotograma). */
  levelRef: MutableRefObject<number>;
  /** Aviso para el usuario tras un intento fallido (permiso, nada oido...). */
  notice: string | null;
  start: () => Promise<void>;
  /** Termina y transcribe lo grabado. */
  finish: () => void;
  /** Termina sin transcribir. */
  cancel: () => void;
}

const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm', 'audio/ogg;codecs=opus'];

// Deteccion de fin de frase (umbral adaptado al ruido de fondo de cada sala).
const SILENCE_AFTER_SPEECH_MS = 1300;
const NO_SPEECH_TIMEOUT_MS = 7000;
const MAX_RECORDING_MS = 25000;
const CALIBRATION_MS = 250;

function pickMime(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return undefined;
  return MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m));
}

function supported(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined' &&
    window.isSecureContext
  );
}

export function useMic(onText: (text: string) => void): Mic {
  const [state, setState] = useState<MicState>('unavailable');
  const [notice, setNotice] = useState<string | null>(null);
  const levelRef = useRef(0);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  const rec = useRef<{
    recorder: MediaRecorder;
    stream: MediaStream;
    context: AudioContext;
    frame: number;
    chunks: Blob[];
    heard: boolean;
    send: boolean;
  } | null>(null);

  // El boton solo aparece si el navegador puede grabar Y el servidor tiene
  // el reconocimiento activo.
  useEffect(() => {
    if (!supported()) return;
    let cancelled = false;
    void fetch('/api/stt/status')
      .then((r) => r.json() as Promise<{ available?: boolean }>)
      .then((s) => {
        if (!cancelled && s.available) setState('idle');
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const release = useCallback(() => {
    const r = rec.current;
    if (!r) return;
    cancelAnimationFrame(r.frame);
    // Apagar las pistas es lo que quita el indicador rojo de "microfono en uso".
    r.stream.getTracks().forEach((t) => t.stop());
    void r.context.close().catch(() => undefined);
    levelRef.current = 0;
  }, []);

  const transcribe = useCallback(async (blob: Blob) => {
    setState('transcribing');
    try {
      const response = await fetch('/api/stt', {
        method: 'POST',
        headers: { 'content-type': blob.type || 'application/octet-stream' },
        body: blob,
      });
      const data = (await response.json()) as { text?: string; error?: string };
      if (!response.ok) {
        setNotice(data.error ?? 'No se pudo entender el audio.');
      } else if (!data.text || data.text.replace(/[\s.,¿?¡!]/g, '').length < 2) {
        setNotice('No te escuché bien. Prueba de nuevo, un poco más cerca del micrófono.');
      } else {
        onTextRef.current(data.text);
      }
    } catch {
      setNotice('No se pudo enviar el audio. Revisa la conexión o escribe tu consulta.');
    } finally {
      setState('idle');
    }
  }, []);

  const stop = useCallback((send: boolean) => {
    const r = rec.current;
    if (!r) return;
    r.send = send;
    if (r.recorder.state !== 'inactive') r.recorder.stop();
    else release();
  }, [release]);

  const start = useCallback(async () => {
    if (rec.current || (state !== 'idle')) return;
    setNotice(null);
    setState('requesting');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      });
    } catch (error) {
      const name = (error as DOMException).name;
      setNotice(
        name === 'NotAllowedError' || name === 'SecurityError'
          ? 'Para hablar con el asistente, permite el uso del micrófono en tu navegador.'
          : name === 'NotFoundError'
            ? 'No se encontró ningún micrófono en este equipo.'
            : 'No se pudo abrir el micrófono.',
      );
      setState('idle');
      return;
    }

    const mimeType = pickMime();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    const AudioCtx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const context = new AudioCtx();
    void context.resume().catch(() => undefined);
    const analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    context.createMediaStreamSource(stream).connect(analyser);
    const samples = new Float32Array(analyser.fftSize);

    const r = { recorder, stream, context, frame: 0, chunks: [] as Blob[], heard: false, send: true };
    rec.current = r;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) r.chunks.push(e.data);
    };
    recorder.onstop = () => {
      release();
      rec.current = null;
      const blob = new Blob(r.chunks, { type: recorder.mimeType || mimeType || 'audio/webm' });
      if (!r.send) {
        setState('idle');
      } else if (!r.heard) {
        setNotice('No te escuché. Pulsa el micrófono y habla cuando se ponga en rojo.');
        setState('idle');
      } else {
        void transcribe(blob);
      }
    };

    // Bucle de nivel y deteccion de silencio.
    const t0 = performance.now();
    let floor = 0.004;
    let lastVoice = 0;
    const tick = () => {
      analyser.getFloatTimeDomainData(samples);
      let sum = 0;
      for (const v of samples) sum += v * v;
      const rms = Math.sqrt(sum / samples.length);
      const now = performance.now();
      const elapsed = now - t0;
      if (elapsed < CALIBRATION_MS) floor = Math.max(floor, rms);
      const threshold = Math.max(0.012, floor * 2.2);
      levelRef.current = Math.min(1, rms * 9);
      if (rms > threshold) {
        r.heard = true;
        lastVoice = now;
      }
      if (
        (r.heard && now - lastVoice > SILENCE_AFTER_SPEECH_MS) ||
        (!r.heard && elapsed > NO_SPEECH_TIMEOUT_MS) ||
        elapsed > MAX_RECORDING_MS
      ) {
        stop(true);
        return;
      }
      r.frame = requestAnimationFrame(tick);
    };

    recorder.start(250);
    setState('listening');
    r.frame = requestAnimationFrame(tick);
  }, [release, state, stop, transcribe]);

  useEffect(() => () => {
    const r = rec.current;
    if (r) {
      r.send = false;
      try {
        r.recorder.stop();
      } catch {
        /* ya parado */
      }
      release();
    }
  }, [release]);

  return {
    state,
    levelRef,
    notice,
    start,
    finish: useCallback(() => stop(true), [stop]),
    cancel: useCallback(() => stop(false), [stop]),
  };
}
